/*
 * VideoShowcase — the "H-Plate in 30 seconds" film on the landing page.
 *
 * The video only downloads when the section comes near the screen, plays
 * muted while at least half of it is visible, and pauses when scrolled
 * away. Visitors who ask for reduced motion, or who have Data Saver on,
 * get the poster and a play button instead of autoplay.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Maximize, Pause, Play, RotateCcw, Volume2, VolumeX } from "lucide-react";
import StoreBadges from "@/components/marketing/StoreBadges";

const VIDEO_SMALL = "/videos/hplate-soon-come-720.mp4";
const VIDEO_LARGE = "/videos/hplate-soon-come-1080.mp4";
const POSTER = "/videos/hplate-soon-come-poster.webp";
const FALLBACK_DURATION = 30.1;

/** Start times (seconds) of each scene in the Soon Come 30s cut. */
const CHAPTERS = [
  { start: 0, title: "The old way", note: "Soon come?" },
  { start: 10, title: "Meet H-Plate", note: "Your bus. Live." },
  { start: 13, title: "Live on the map", note: "See where your bus is" },
  { start: 15, title: "The fare first", note: "Government-regulated, in EC$" },
  { start: 17, title: "A ping when it's close", note: "Wait inside, not by the road" },
  { start: 19, title: "Drivers see you", note: "Every booking on their route" },
  { start: 22, title: "Then and now", note: "It's here" },
];

type WebkitVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void };

function formatTime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function prefersNoAutoplay(): boolean {
  if (typeof window === "undefined") return true;
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  const saveData =
    (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;
  return reduced || saveData;
}

/**
 * Landing-page film with chapter navigation, custom controls and a
 * download prompt when it ends.
 */
export default function VideoShowcase() {
  const sectionRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const chaptersRef = useRef<HTMLOListElement>(null);
  const userPausedRef = useRef(false);
  const autoplayOffRef = useRef(true);
  const inViewRef = useRef(false);

  const [src, setSrc] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [ended, setEnded] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(FALLBACK_DURATION);

  const play = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setEnded(false);
    void video.play().catch(() => setPlaying(false));
  }, []);

  /* Load the right file once the section is near, then autoplay in view. */
  useEffect(() => {
    autoplayOffRef.current = prefersNoAutoplay();
    const section = sectionRef.current;
    const card = cardRef.current;
    if (!section || !card || typeof IntersectionObserver === "undefined") {
      setSrc(VIDEO_SMALL);
      return;
    }

    const loader = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSrc(window.matchMedia("(min-width: 1024px) and (min-resolution: 1.5dppx)").matches ? VIDEO_LARGE : VIDEO_SMALL);
          loader.disconnect();
        }
      },
      { rootMargin: "600px 0px" },
    );
    loader.observe(section);

    const visibility = new IntersectionObserver(
      ([entry]) => {
        const video = videoRef.current;
        inViewRef.current = entry.intersectionRatio >= 0.5;
        if (!video) return;
        if (inViewRef.current) {
          if (!autoplayOffRef.current && !userPausedRef.current && video.paused && !video.ended) {
            play();
          }
        } else if (!video.paused) {
          video.pause();
        }
      },
      { threshold: [0, 0.5] },
    );
    visibility.observe(card);

    return () => {
      loader.disconnect();
      visibility.disconnect();
    };
  }, [play]);

  /* Smooth progress while playing; timeupdate alone is too coarse. */
  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    const tick = () => {
      const video = videoRef.current;
      if (video) setCurrent(video.currentTime);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused || video.ended) {
      userPausedRef.current = false;
      play();
    } else {
      userPausedRef.current = true;
      video.pause();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
    if (!video.muted && (video.paused || video.ended)) {
      userPausedRef.current = false;
      if (video.ended) video.currentTime = 0;
      play();
    }
  };

  const seekTo = (time: number, autoplay = true) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.min(Math.max(time, 0), duration - 0.05);
    setCurrent(video.currentTime);
    if (autoplay) {
      userPausedRef.current = false;
      play();
    }
  };

  const enterFullscreen = () => {
    const video = videoRef.current as WebkitVideo | null;
    const card = cardRef.current;
    if (card?.requestFullscreen && document.fullscreenEnabled) {
      void card.requestFullscreen().catch(() => video?.webkitEnterFullscreen?.());
    } else {
      video?.webkitEnterFullscreen?.();
    }
  };

  const activeIndex = CHAPTERS.reduce((acc, ch, i) => (current >= ch.start ? i : acc), 0);
  const chapterProgress = (i: number) => {
    const start = CHAPTERS[i].start;
    const end = CHAPTERS[i + 1]?.start ?? duration;
    if (current <= start) return 0;
    return Math.min(1, (current - start) / (end - start));
  };
  const progressPct = duration > 0 ? (current / duration) * 100 : 0;

  /* On phones the scene row scrolls sideways; keep the playing scene in view without moving the page. */
  useEffect(() => {
    const list = chaptersRef.current;
    if (!list || list.scrollWidth <= list.clientWidth || (!playing && current === 0)) return;
    const item = list.children[activeIndex] as HTMLElement | undefined;
    if (!item) return;
    list.scrollTo({ left: Math.max(0, item.offsetLeft - 16), behavior: "smooth" });
  }, [activeIndex]);

  return (
    <section
      ref={sectionRef}
      id="watch"
      aria-labelledby="watch-heading"
      className="scroll-mt-20 py-20 md:py-28 bg-gradient-to-b from-white via-[#f3f8f5] to-white"
    >
      <div className="container grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-10 lg:items-center">
        <div className="lg:col-span-5 lg:col-start-1 lg:row-start-1 lg:self-end">
          <p className="text-sm font-semibold uppercase tracking-[0.14em] text-emerald-700">Watch</p>
          <h2 id="watch-heading" className="mt-3 font-display text-4xl md:text-5xl tracking-tight text-slate-900">
            H-Plate in 30 seconds
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">
            For decades, finding a bus in Dominica meant waiting by the road
            and hoping. Tap a scene to jump to it.
          </p>
        </div>

        <div className="lg:col-span-7 lg:col-start-6 lg:row-start-1 lg:row-span-2">
          <div
            ref={cardRef}
            className="relative mx-auto w-full max-w-[460px] lg:max-w-[520px] aspect-[4/5] overflow-hidden rounded-[2rem] bg-[#eef3f1] shadow-[0_30px_80px_-30px_rgba(4,36,27,0.45)] ring-1 ring-black/5 [&:fullscreen]:max-w-none [&:fullscreen]:rounded-none [&:fullscreen]:bg-black"
          >
            <video
              ref={videoRef}
              src={src ?? undefined}
              poster={POSTER}
              muted
              playsInline
              preload="metadata"
              aria-label="H-Plate film: Soon come? It's here. The bus app for Dominica, showing live buses on the map, the fare before you ride and a ping when your bus is close."
              className="h-full w-full object-contain cursor-pointer"
              onClick={togglePlay}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => {
                setPlaying(false);
                setEnded(true);
                setCurrent(duration);
              }}
              onLoadedMetadata={(e) => {
                setDuration(e.currentTarget.duration || FALLBACK_DURATION);
                if (inViewRef.current && !autoplayOffRef.current && !userPausedRef.current) play();
              }}
              onTimeUpdate={(e) => {
                if (!playing) setCurrent(e.currentTarget.currentTime);
              }}
              onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
            />

            {!playing && !ended && (
              <button
                type="button"
                onClick={togglePlay}
                aria-label="Play video"
                className="absolute inset-0 m-auto flex h-20 w-20 items-center justify-center rounded-full bg-white/90 text-emerald-800 shadow-lg backdrop-blur border-0 cursor-pointer hover:bg-white transition-colors"
              >
                <Play className="h-8 w-8 translate-x-0.5" fill="currentColor" />
              </button>
            )}

            {playing && muted && (
              <button
                type="button"
                onClick={toggleMute}
                className="absolute top-4 right-4 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-black/65 text-white text-sm font-semibold backdrop-blur border-0 cursor-pointer hover:bg-black/75 transition-colors"
              >
                <VolumeX className="h-4 w-4" aria-hidden />
                Tap for sound
              </button>
            )}

            {ended && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-white/85 backdrop-blur-md px-6 text-center">
                <img src="/logo-transparent.png" alt="" width={56} height={56} className="h-14 w-14 object-contain" />
                <div>
                  <p className="font-display text-3xl text-slate-900">Soon come? It's here.</p>
                  <p className="mt-2 text-slate-600">Free in Dominica, on iPhone and Android.</p>
                </div>
                <StoreBadges className="justify-center" />
                <button
                  type="button"
                  onClick={() => seekTo(0)}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-slate-300 bg-white text-[15px] font-semibold text-slate-800 cursor-pointer hover:border-slate-400 transition-colors"
                >
                  <RotateCcw className="h-4 w-4" aria-hidden />
                  Watch again
                </button>
              </div>
            )}

            {!ended && (
              <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 px-4 pb-4 pt-10 bg-gradient-to-t from-black/45 to-transparent text-white">
                <button
                  type="button"
                  onClick={togglePlay}
                  aria-label={playing ? "Pause video" : "Play video"}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 border-0 text-white cursor-pointer hover:bg-white/25 transition-colors"
                >
                  {playing ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="h-4 w-4 translate-x-px" fill="currentColor" />}
                </button>
                <div className="relative h-10 flex-1 flex items-center">
                  <div className="h-1 w-full rounded-full bg-white/30" aria-hidden>
                    <div className="h-1 rounded-full bg-white" style={{ width: `${progressPct}%` }} />
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={duration}
                    step={0.1}
                    value={Math.min(current, duration)}
                    onChange={(e) => seekTo(Number(e.target.value), false)}
                    aria-label="Seek"
                    aria-valuetext={`${formatTime(current)} of ${formatTime(duration)}`}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
                <span className="text-xs tabular-nums text-white/85 w-[68px] text-right">
                  {formatTime(current)} / {formatTime(duration)}
                </span>
                <button
                  type="button"
                  onClick={toggleMute}
                  aria-label={muted ? "Turn sound on" : "Mute"}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 border-0 text-white cursor-pointer hover:bg-white/25 transition-colors"
                >
                  {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={enterFullscreen}
                  aria-label="Full screen"
                  className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 border-0 text-white cursor-pointer hover:bg-white/25 transition-colors"
                >
                  <Maximize className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="lg:col-span-5 lg:col-start-1 lg:row-start-2 lg:self-start">
          <ol ref={chaptersRef} className="relative flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible -mx-4 px-4 lg:mx-0 lg:px-0 pb-2 lg:pb-0 snap-x scroll-px-4 lg:scroll-px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {CHAPTERS.map((chapter, i) => {
              const active = i === activeIndex && (playing || current > 0) && !ended;
              return (
                <li key={chapter.title} className="snap-start shrink-0 lg:shrink">
                  <button
                    type="button"
                    onClick={() => seekTo(chapter.start)}
                    aria-current={active ? "step" : undefined}
                    className={`group relative w-full min-w-[168px] lg:min-w-0 overflow-hidden rounded-xl border px-4 py-3 text-left cursor-pointer transition-colors ${
                      active
                        ? "border-emerald-600 bg-white shadow-sm"
                        : "border-slate-200 bg-white/70 hover:border-slate-300 hover:bg-white"
                    }`}
                  >
                    <span className="flex items-baseline justify-between gap-3">
                      <span className={`text-[15px] font-semibold ${active ? "text-emerald-800" : "text-slate-800"}`}>
                        {chapter.title}
                      </span>
                      <span className="text-xs tabular-nums text-slate-400">{formatTime(chapter.start)}</span>
                    </span>
                    <span className="mt-0.5 block text-[13px] text-slate-500">{chapter.note}</span>
                    <span
                      className="absolute left-0 bottom-0 h-[3px] bg-emerald-600 transition-[width] duration-100"
                      style={{ width: active ? `${chapterProgress(i) * 100}%` : 0 }}
                      aria-hidden
                    />
                  </button>
                </li>
              );
            })}
          </ol>
        </div>

      </div>
    </section>
  );
}
